/**
 * Connector card component.
 * Reusable card for displaying connector information in grid/list views.
 */

// React Imports
import { Card, CardContent, CardActions, Typography, Box, Chip, IconButton, Tooltip } from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { ConnectorIcon } from './ConnectorIcon';
import type { ConnectorDto } from '../../types/connector';

export interface ConnectorCardProps {
  connector: ConnectorDto;
  viewMode?: 'grid' | 'list';
  onConnect?: (connector: ConnectorDto) => void;
  onView?: (connector: ConnectorDto) => void;
  onSettings?: (connector: ConnectorDto) => void;
}

/**
 * Connector card component.
 */
export const ConnectorCard = ({
  connector,
  viewMode = 'grid',
  onConnect,
  onView,
  onSettings,
}: ConnectorCardProps) => {
  const getCategoryColor = (category?: string) => {
    const cat = category?.toLowerCase() || '';
    if (cat.includes('data validation')) return 'info';
    if (cat.includes('communication')) return 'primary';
    if (cat.includes('storage')) return 'success';
    if (cat.includes('healthcare')) return 'error';
    if (cat.includes('document processing')) return 'warning';
    if (cat.includes('task management')) return 'secondary';
    return 'default';
  };

  if (viewMode === 'list') {
    return (
      <Card
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 2,
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 4,
          },
        }}
        onClick={() => onView?.(connector)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <ConnectorIcon connectorKey={connector.key} category={connector.category} sx={{ fontSize: 40 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {connector.name}
            </Typography>
            {connector.vendor && (
              <Typography variant="caption" color="text.secondary">
                {connector.vendor}
              </Typography>
            )}
            {connector.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {connector.description.substring(0, 100)}
                {connector.description.length > 100 ? '...' : ''}
              </Typography>
            )}
          </Box>
          {connector.category && (
            <Chip
              label={connector.category}
              color={getCategoryColor(connector.category)}
              size="small"
              variant="outlined"
            />
          )}
          <Chip label={`${connector.capabilityCount} capabilities`} size="small" variant="outlined" />
          <Chip label={`${connector.instanceCount} instances`} size="small" variant="outlined" />
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onView && (
              <Tooltip title="View Details">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onView(connector); }}>
                  <ViewIcon />
                </IconButton>
              </Tooltip>
            )}
            {onConnect && (
              <Tooltip title="Connect">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onConnect(connector);
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Card>
    );
  }

  // Grid view
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-4px)',
        },
      }}
      onClick={() => onView?.(connector)}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <ConnectorIcon
            connectorKey={connector.key}
            category={connector.category}
            sx={{ fontSize: 48, color: 'primary.main' }}
          />
          {connector.category && (
            <Chip
              label={connector.category}
              color={getCategoryColor(connector.category)}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
          {connector.name}
        </Typography>

        {connector.vendor && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            by {connector.vendor}
          </Typography>
        )}

        {connector.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flex: 1 }}>
            {connector.description.substring(0, 120)}
            {connector.description.length > 120 ? '...' : ''}
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 'auto' }}>
          <Chip
            label={`${connector.capabilityCount} capabilities`}
            size="small"
            variant="outlined"
            color="primary"
          />
          {connector.instanceCount > 0 && (
            <Chip label={`${connector.instanceCount} instances`} size="small" variant="outlined" />
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onView && (
            <Tooltip title="View Details">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onView(connector);
                }}
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>
          )}
          {onSettings && (
            <Tooltip title="Settings">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onSettings(connector);
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {onConnect && (
          <Tooltip title="Create Instance">
            <IconButton
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                onConnect(connector);
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
};
